import { useEffect, useState } from 'react';

/**
 * Measures the pixel-accurate bottom edge of a header block (greeting +
 * search + chips + location badge) so downstream layout can position
 * the expanded map EXACTLY below the last pill instead of guessing
 * with a hardcoded constant.
 *
 * Params:
 *   groupRef  — ref to the DOM node whose bottom we want to track.
 *   fallback  — value returned before the first measurement lands.
 * Returns:
 *   headerBottom — bounding rect bottom (+ 4 px breathing gap).
 */
export function useHeaderBottomMeasure(groupRef, fallback) {
  const [headerBottom, setHeaderBottom] = useState(fallback);
  useEffect(() => {
    if (!groupRef.current) return undefined;
    const measure = () => {
      const rect = groupRef.current?.getBoundingClientRect();
      if (rect) setHeaderBottom(rect.bottom + 4); // breathing gap
    };
    measure();
    // Selected chips can wrap without a window resize.
    const observer = new ResizeObserver(measure);
    observer.observe(groupRef.current);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
    };
  }, [groupRef]);
  return headerBottom;
}
